import {
  IBaseDataLayer,
  IDataLayerVariable,
  PageType,
} from "@almamedia/amr-adtech-stack/lib/services/dataLayer";

// Declare custom IL globals as best as we can
declare global {
  interface Window {
    ilAdSettings?: Record<string, unknown>;
    abcTestVariant?: unknown;
    abcdTestVariant?: unknown;
    alternativeAbTestVariant?: unknown;
    ILUserSegment?: unknown;
    ilWeatherAdSettings?: {
      airTemperature?: string;
      precipitation?: string;
    };
  }
}

interface ICustomVariables extends IBaseDataLayer {
  viewPort: IDataLayerVariable<number>;
  destinationURL: IDataLayerVariable<string>;
  subCategory: IDataLayerVariable<string>;
  validate: IDataLayerVariable<boolean>;

  department: IDataLayerVariable<string>;
  mainDepartment: IDataLayerVariable<string>;
  pageClass: IDataLayerVariable<string>;
  pageTitle: IDataLayerVariable<string>;
  pageLocationPathName: IDataLayerVariable<string>;
  publisher: IDataLayerVariable<string>;
  adDemo: IDataLayerVariable<string>;
  keyWords: IDataLayerVariable<string | null>;
  author: IDataLayerVariable<string>;
  originalTitle: IDataLayerVariable<string | null>;
  authors: IDataLayerVariable<string>;
  published: IDataLayerVariable<string>;
  publishDate: IDataLayerVariable<string>;
  publishHour: IDataLayerVariable<string>;
  publishWeek: IDataLayerVariable<number | string>;
  modified: IDataLayerVariable<string>;
  userAgent: IDataLayerVariable<string>;
  fb: IDataLayerVariable<string>;
  abTestVariant: IDataLayerVariable<string>;
  abcTestVariant: IDataLayerVariable<unknown>;
  abcdTestVariant: IDataLayerVariable<unknown>;
  alternativeAbTestVariant: IDataLayerVariable<unknown>;
  ILUserSegment: IDataLayerVariable<unknown>;
  loginStatus: IDataLayerVariable<string>;
  source: IDataLayerVariable<string>;
  dayName: IDataLayerVariable<string>;

  // IL Weather
  airTemperature: IDataLayerVariable<number | undefined>;
  precipitation: IDataLayerVariable<number | undefined>;
}

type DataLayerImplementation = {
  name: string;
  id: string;
  data: ICustomVariables;
};

const dataLayer: DataLayerImplementation = {
  name: "Iltalehti data layer",
  id: "il-dl",
  data: {
    department: {
      // unique name
      name: "department", // friendly name
      get() {
        return window?.digitalData?.page?.category?.subCategory1 ?? "error";
      },
    },
    mainDepartment: {
      // unique name
      name: "main department", // friendly name
      get() {
        return window?.digitalData?.page?.category?.primaryCategory ?? "error";
      },
    },
    primaryCategory: {
      // unique name | default etusivu
      name: "primary category", // friendly name
      get() {
        if (window.ilAdSettings && window.ilAdSettings.mainCategory) {
          return window.ilAdSettings.mainCategory;
        }
        return (
          window?.digitalData?.page?.category?.primaryCategory ?? "etusivu"
        );
      },
    },
    subCategory: {
      // unique name
      name: "sub category", // friendly name
      get() {
        if (window.ilAdSettings && window.ilAdSettings.category) {
          return window.ilAdSettings.category;
        }
        return window?.digitalData?.page?.category?.subCategory1 ?? "";
      },
    },
    subCategory1: {
      // unique name
      name: "sub category 1", // friendly name
      get() {
        if (window.ilAdSettings && window.ilAdSettings.subCategory1) {
          return window.ilAdSettings.subCategory1;
        }
        if (window.ilAdSettings && window.ilAdSettings.category) {
          return window.ilAdSettings.category;
        }
        return window?.digitalData?.page?.category?.subCategory1 ?? "";
      },
    },
    subCategory2: {
      // unique name
      name: "sub category 2", // friendly name
      get() {
        if (
          window.ilAdSettings &&
          typeof window.ilAdSettings.subCategory2 === "string"
        ) {
          return window.ilAdSettings.subCategory2;
        }
        return window?.digitalData?.page?.category?.subCategory2 ?? "";
      },
    },
    pageTags: {
      // unique name
      name: "page tags", // friendly name
      get() {
        let pageTags = null;
        const tags = [];
        if (window.ilAdSettings && window.ilAdSettings.keywords) {
          pageTags = window.ilAdSettings.keywords;
        } else if (window?.digitalData?.page?.category?.tags) {
          pageTags = window.digitalData.page.category.tags;
        }

        if (pageTags !== null) {
          for (let i = 0; i < pageTags.length; i += 1) {
            tags.push(pageTags[i].toLowerCase());
          }
        }

        return tags;
      },
    },
    pageClass: {
      // unique name
      name: "page class", // friendly name
      get() {
        if (window.ilAdSettings && window.ilAdSettings.pageType) {
          return window.ilAdSettings.pageType;
        }
        return window?.digitalData?.page?.attributes?.pageClass ?? "error";
      },
    },
    pageId: {
      // unique name
      name: "page ID", // friendly name
      get() {
        if (
          window.ilAdSettings &&
          window.ilAdSettings.pageType &&
          window.ilAdSettings.pageType === "dep-start-page"
        ) {
          return window.ilAdSettings.category;
        }
        if (window.ilAdSettings && window.ilAdSettings.articleId) {
          return window.ilAdSettings.articleId;
        }
        return window?.digitalData?.page?.pageInfo?.pageID ?? "error";
      },
    },
    pageTitle: {
      name: "page Title", // friendly name
      get() {
        return window?.digitalData?.page?.pageInfo?.pageTitle ?? "error";
      },
    },
    pageLocationPathName: {
      // unique name
      name: "page Path Name", // friendly name
      get() {
        return window?.digitalData?.page?.pageInfo?.locationPathName ?? "error";
      },
    },
    publisher: {
      // unique name
      name: "publisher", // friendly name
      get() {
        return window?.digitalData?.page?.pageInfo?.publisher ?? "error";
      },
    },
    sysEnv: {
      // unique name
      name: "sysEnv mobile or desktop", // friendly name
      get() {
        return window?.digitalData?.page?.pageInfo?.sysEnv ?? "error";
      },
    },
    locationHref: {
      // unique name
      name: "location Href url", // friendly name
      get() {
        // return ((digitalData && digitalData.page && digitalData.page.pageInfo && digitalData.page.pageInfo.locationHref) ? digitalData.page.pageInfo.locationHref : 'error');
        return window.location.href;
      },
    },
    adDemo: {
      // unique name
      name: "ad demo", // friendly name
      get() {
        return window?.digitalData?.page?.attributes?.adDemo ?? "";
      },
    },
    section: {
      name: "section for ad load",
      get() {
        // TODO: Unused?
        // const pathName = window.location.pathname;
        let dataLayerAdSection = "";
        let dataLayerViewport = "";
        let digitalDataPrimaryCategory;
        let digitalDataSubCategory;
        let digitalDataTags;
        const desktopWidth = 980;
        let digitalDataPageClass;
        const sponsoredCategories = ["apuanukahtamiseen"];

        if (window.ilAdSettings) {
          digitalDataPrimaryCategory = window.ilAdSettings.mainCategory;
          digitalDataSubCategory = window.ilAdSettings.category;
          digitalDataPageClass = window.ilAdSettings.pageType;
        } else {
          digitalDataPrimaryCategory =
            window?.digitalData?.page?.category?.primaryCategory ?? "uutiset";
          digitalDataSubCategory =
            window?.digitalData?.page?.category?.subCategory1 ?? "";
          digitalDataPageClass =
            window?.digitalData?.page?.attributes?.pageClass ?? "";
        }

        if (window.ilAdSettings && window.ilAdSettings.keywords) {
          digitalDataTags = window.ilAdSettings.keywords;
        } else if (window?.digitalData?.page?.category?.tags) {
          digitalDataTags = window.digitalData.page.category.tags ?? "";
        }

        // Check if the current page is article
        let articlePageView = "";
        if (
          digitalDataPageClass === "article-page" ||
          digitalDataPageClass === "article"
        ) {
          // Check if sponsored article
          let sponsoredArticle = "";
          if (digitalDataTags !== "") {
            if (
              digitalDataTags.indexOf("sponsored_content") > -1 ||
              digitalDataTags.indexOf("sponsored content") > -1 ||
              digitalDataTags.indexOf("Sponsored content") > -1 ||
              sponsoredCategories.indexOf(digitalDataSubCategory) !== -1
            ) {
              sponsoredArticle = "_sponsored";
            }
          }

          articlePageView = `_article${sponsoredArticle}`;
        }

        /*
                          if (digitalDataPrimaryCategory === 'digi' || digitalDataPrimaryCategory === 'viihde' || digitalDataPrimaryCategory === 'uutiset') {
                      desktopWidth = 620;
                    }
                    */

        if (
          window.location.pathname.indexOf(
            "/tv-ja-leffat/812eaa8d-b3f6-4daa-9cc5-dd978c3dccb6"
          ) !== -1
        ) {
          digitalDataPrimaryCategory = "null";
        }

        if (digitalDataPrimaryCategory === "plus")
          digitalDataPrimaryCategory = "uutiset";

        if (window.innerWidth > 1279) {
          dataLayerViewport = "_desktop_wide";
        } else if (window.innerWidth > desktopWidth) {
          dataLayerViewport = "_desktop";
        } else {
          dataLayerViewport = "_mobile";
        }
        /*
                    if (digitalDataSubCategory === 'tyoelama' || digitalDataSubCategory === 'talous') {
                      dataLayerAdSection = 'tyoelama-talous' + articlePageView;
                    } else {
                      dataLayerAdSection = digitalDataPrimaryCategory + dataLayerViewport + articlePageView;
                    } */

        dataLayerAdSection =
          digitalDataPrimaryCategory + dataLayerViewport + articlePageView;

        // *****************************
        // Block ads from array of paths
        // *****************************
        const blockedURLs = [
          "/kotimaa/a250342c-b4b1-4140-8e75-62932dad4ff8_u0.shtml",
        ];
        if (blockedURLs.indexOf(window.location.pathname) === 0)
          dataLayerAdSection = "noads";

        /*
                    window.ALMA.ad.ads = (dataLayerAdSection === 'noads') ? false : true;        
                    window.ALMA.ad.log('window.ALMA.ad.ads', window.ALMA.ad.ads);
                    */

        // *****************************

        return dataLayerAdSection;
      },
    },
    ads: {
      name: "enable / disable ads",
      get() {
        // True enables ads, false disables.
        const digitalDataPrimaryCategory =
          window?.digitalData?.page?.category?.primaryCategory ?? "noads";

        if (
          digitalDataPrimaryCategory === "(none)" ||
          digitalDataPrimaryCategory === ""
        ) {
          return false;
        }
        return true;
      },
    },
    keyWords: {
      // unique name
      name: "keywords for a page, meta words", // friendly name
      get() {
        return document.getElementsByName("keywords")[0] !== undefined
          ? document.getElementsByName("keywords")[0].getAttribute("content")
          : "";
      },
    },
    author: {
      // article author
      name: "author",
      get() {
        // return ((digitalData && digitalData.page && digitalData.page.attributes && digitalData.page.attributes.authors) ? digitalData.page.attributes.authors : 'unknown');
        let pageTags = null;

        if (window.ilAdSettings && window.ilAdSettings.keywords) {
          pageTags = window.ilAdSettings.keywords;
        } else if (window?.digitalData?.page?.category?.tags) {
          pageTags = window.digitalData.page.category.tags;
        }

        if (pageTags !== null) {
          const regx = /author_/;
          let keywords = pageTags;
          keywords = keywords.join();
          keywords = keywords.replace(/\s+/g, "");
          keywords = keywords.split(",");
          let author = keywords
            .filter(function (a: any) {
              return a.match(regx);
            })
            .toString();
          if (author.charAt(author.length - 1) === "_") {
            author = author.substr(0, author.length - 1);
          }
          return author.replace(/author_/g, "");
        }
        return "";
      },
    },
    originalTitle: {
      // unique name
      name: "originalTitle", // friendly name
      get() {
        return document.querySelector('meta[property="st:title"]')
          ? document
              .querySelector('meta[property="st:title"]')!
              .getAttribute("content")
          : "no originalTitle";
      },
    },
    authors: {
      // article author
      name: "authors",
      get() {
        if (window?.digitalData?.page?.attributes?.authors) {
          const authorsArray = window.digitalData.page.attributes.authors;
          let authors = "";
          for (let i = 0; i < authorsArray.length; i += 1) {
            authors += authorsArray[i];
            if (i < authorsArray.length - 1) {
              authors += ", ";
            }
          }
          return authors;
        }
        return "";
      },
    },
    published: {
      name: "published date iso string",
      get() {
        // format: 2018-11-08T15:42:47+02:00
        return window?.digitalData?.page?.pageInfo?.issueDate ?? "";
      },
    },
    publishDate: {
      name: "publish date",
      get() {
        // format: 2018-11-08
        if (window?.digitalData?.page?.pageInfo?.issueDate) {
          return window.digitalData.page.pageInfo.issueDate.split("T")[0];
        }
        return "";
      },
    },
    publishHour: {
      name: "publish hour",
      get() {
        // format: 08
        if (window?.digitalData?.page?.pageInfo?.issueDate) {
          return window.digitalData.page.pageInfo.issueDate.slice(11, 13);
        }
        return "";
      },
    },
    publishWeek: {
      name: "publish hour",
      get() {
        // format: 45
        if (window?.digitalData?.page?.pageInfo?.issueDate) {
          const { issueDate } = window.digitalData.page.pageInfo;
          const year = Number(issueDate.substring(0, 4));
          const month = Number(issueDate.substring(5, 7)) - 1;
          const day = Number(issueDate.substring(8, 10));
          const d = new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
          d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
          const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));

          // Added getTimes
          const weekNo = Math.ceil(
            ((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7
          );
          return weekNo;
        }
        return "";
      },
    },
    modified: {
      name: "modified date iso string",
      get() {
        // format: 2018-11-08T15:42:47+02:00
        return window?.digitalData?.page?.pageInfo?.modifiedDate ?? "";
      },
    },
    userAgent: {
      name: "userAgent",
      get() {
        return window.navigator.userAgent;
      },
    },
    fb: {
      name: "fb",
      get() {
        return window.navigator.userAgent.includes(
          "FB" || "FBAN" || "FBAV" || "FBIOS"
        )
          ? "1"
          : "0";
      },
    },
    abTestVariant: {
      name: "AB test variant (A or B)",
      get() {
        // format: A
        return (
          window?.digitalData?.page?.attributes?.testVariant ?? "undefined"
        );
      },
    },
    abcTestVariant: {
      name: "ABC test variant (A, B, or C)",
      get() {
        return window.abcTestVariant;
      },
    },
    abcdTestVariant: {
      name: "ABCD test variant (A, B, C or D)",
      get() {
        return window.abcdTestVariant;
      },
    },
    alternativeAbTestVariant: {
      name: "Alternative AB test variant (A or B)",
      get() {
        return window.alternativeAbTestVariant;
      },
    },
    ILUserSegment: {
      name: "IL lightFM user segment (0-16)",
      get() {
        return window.ILUserSegment || "None";
      },
    },
    cdime: {
      name: "Alma tunnus cdime",
      get() {
        const ddlProfile =
          window?.digitalData?.user?.[0]?.profile?.[0]?.profileInfo;
        return ddlProfile?.cdime ?? "None";
      },
    },
    loginStatus: {
      name: "Alma tunnus login status",
      get() {
        const cdime =
          window?.digitalData?.user?.[0]?.profile?.[0]?.profileInfo?.cdime;
        return cdime ? "LoggedIn" : "LoggedOut";
      },
    },
    source: {
      name: "Source of the article",
      get() {
        return window?.digitalData?.page?.attributes?.source ?? "";
      },
    },
    dayName: {
      name: "short name of the day",
      get() {
        const dayIndex = new Date().getDay();
        const dayNames = ["Su", "Ma", "Ti", "Ke", "To", "Pe", "La"];
        return dayNames[dayIndex];
      },
    },
    validate: {
      // unique name
      name: "Validate", // friendly name
      get() {
        const pageClass =
          window?.ilAdSettings?.pageType ??
          window?.digitalData?.page?.attributes?.pageClass ??
          window?.digitalData?.page?.attributes?.pageClass ??
          null;
        return pageClass === "article-page";
      },
    },
    destinationURL: {
      name: "destination url",
      get: (): string => {
        return window.digitalData?.page?.pageInfo?.locationHref ?? "";
      },
    },
    viewPort: {
      name: "viewport",
      get: (): number => {
        return window.digitalData?.page?.attributes?.width ?? window.innerWidth;
      },
    },
    accountType: {
      name: "user account type",
      get: (): string | undefined => {
        const ddlProfile =
          window?.digitalData?.user?.[0]?.profile?.[0]?.profileInfo;
        return ddlProfile?.accountType;
      },
    },
    alma_id: {
      name: "alma_id hash",
      get: (): string | undefined => {
        const ddlProfile =
          window?.digitalData?.user?.[0]?.profile?.[0]?.profileInfo;
        return ddlProfile?.almaIDHash;
      },
    },
    amid: {
      name: "alma master id",
      get: (): string | undefined => {
        try {
          return window.localStorage.getItem("gravitoOPUid") ?? undefined;
        } catch (e) {
          return undefined;
        }
      },
    },
    cdimu: {
      name: "sha256 hash of email",
      get: (): string | undefined => {
        const ddlProfile =
          window?.digitalData?.user?.[0]?.profile?.[0]?.profileInfo;
        return ddlProfile?.cdimu;
      },
    },
    airTemperature: {
      name: "airTemperature",
      get: (): number | undefined => {
        return window?.ilWeatherAdSettings?.airTemperature
          ? parseFloat(window.ilWeatherAdSettings.airTemperature)
          : undefined;
      },
    },
    precipitation: {
      name: "precipitation",
      get: (): number | undefined => {
        return window?.ilWeatherAdSettings?.precipitation
          ? parseFloat(window.ilWeatherAdSettings.precipitation)
          : undefined;
      },
    },
    cid: {
      name: "content id",
      get: (): string => {
        if (
          window.ilAdSettings &&
          window.ilAdSettings.articleId &&
          typeof window.ilAdSettings.articleId === "string"
        ) {
          return window.ilAdSettings.articleId;
        }
        return window?.digitalData?.page?.attributes?.content?.cid ?? "";
      },
    },
    pageType: {
      name: "page type",
      get: (): PageType => {
        let pageType = "";
        if (window.ilAdSettings && window.ilAdSettings.pageType) {
          pageType = window.ilAdSettings.pageType as string;
        } else {
          pageType =
            window?.digitalData?.page?.attributes?.pageClass ?? "error";
        }
        if (pageType === "article-page") {
          return PageType.Article;
        }
        return "error" as PageType;
      },
    },
    issueDate: {
      name: "article issue date",
      get: (): string => {
        if (
          window.ilAdSettings &&
          window.ilAdSettings.publishedAt &&
          typeof window.ilAdSettings.publishedAt === "string"
        ) {
          return window.ilAdSettings.publishedAt;
        }
        return window?.digitalData?.page?.pageInfo?.issueDate ?? "";
      },
    },
  },
};

export default dataLayer;
export type { ICustomVariables };
